import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Package, Copy, Check, Rocket, Hand, ListChecks, Plug, AlertTriangle } from 'lucide-react';
import { getTrackingPackage, setLaunchMode, markCampaignLaunched, setPromoCode, deployCampaignTracking } from '../utils/api';

const CopyField = ({ field }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(field.value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="p-3.5 rounded-lg bg-black border border-divider" data-testid={`pkg-field-${field.key}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-xs text-muted uppercase tracking-wide font-semibold">{field.label}</p>
        {!field.not_ready && field.value && (
          <button onClick={copy} data-testid={`pkg-copy-${field.key}`} className="text-gray hover:text-lime shrink-0" title="Copy">
            {copied ? <Check size={14} className="text-lime" /> : <Copy size={14} />}
          </button>
        )}
      </div>
      {field.not_ready ? (
        <p className="text-xs text-orange flex items-start gap-1" data-testid={`pkg-notready-${field.key}`}>
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          Tracking link not ready. Add your landing page or website URL in <Link to="/my-brand" className="underline">My Brand</Link>.
        </p>
      ) : (
        <code className="text-sm text-lime break-all block">{field.value}</code>
      )}
      <p className="text-[13px] text-gray mt-1.5 leading-relaxed">{field.explain}</p>
    </div>
  );
};

const MODES = [
  { id: 'manual', label: 'Manual Launch', icon: Hand, desc: 'You copy, paste and launch everything yourself. Full control.' },
  { id: 'guided', label: 'Guided Launch', icon: ListChecks, desc: 'Step-by-step instructions for Meta and Google, with your names and links prefilled.' },
  { id: 'connected', label: 'Connected Launch', icon: Plug, desc: 'One-click launch through connected ad accounts.', soon: true },
];

export const TrackingPackagePanel = ({ campaignId }) => {
  const [pkg, setPkg] = useState(null);
  const [promo, setPromo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await getTrackingPackage(campaignId);
      setPkg(data);
      setPromo(data.promo_code || '');
    } catch (e) {
      console.error('package load failed', e);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const handleDeploy = async () => {
    setBusy(true);
    try { await deployCampaignTracking(campaignId); await load(); } finally { setBusy(false); }
  };

  const handleMode = async (mode) => {
    if (mode === 'connected') return;
    try {
      await setLaunchMode(campaignId, mode);
      setPkg((p) => ({ ...p, launch_mode: mode }));
    } catch (e) { flash(e.response?.data?.detail || 'Could not switch mode'); }
  };

  const handleLaunched = async () => {
    setBusy(true);
    try {
      await markCampaignLaunched(campaignId);
      await load();
      flash('🚀 Marked as launched — enter your numbers on the Campaign Performance card as results come in.');
    } catch (e) { flash(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const savePromo = async () => {
    if (!promo.trim()) return;
    try { await setPromoCode(campaignId, promo); flash('Promo code saved to your package.'); } catch (e) { flash('Save failed'); }
  };

  if (!pkg) return null;

  return (
    <div className="bg-card-bg border border-divider rounded-xl p-6 mb-8" data-testid="tracking-package-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h3 className="text-white font-bold text-xl flex items-center gap-2">
          <Package size={18} className="text-orange" /> Campaign Tracking Package
        </h3>
        {pkg.has_deployment && (
          pkg.status === 'launched' ? (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-lime/15 text-lime border border-lime/40" data-testid="pkg-launched-badge">
              🚀 Launched {pkg.launched_at ? new Date(pkg.launched_at).toLocaleDateString() : ''}
            </span>
          ) : (
            <button onClick={handleLaunched} disabled={busy} data-testid="pkg-mark-launched-btn"
              className="px-4 py-2 rounded-lg bg-orange text-black font-bold text-sm hover:bg-orange/90 disabled:opacity-50 flex items-center gap-1.5">
              <Rocket size={14} /> Mark as Launched
            </button>
          )
        )}
      </div>
      <p className="text-sm text-gray mb-5">
        Everything you need to launch this campaign with tracking that works — GritClicks matches your calls, bookings, and revenue back to the campaign that created them.
      </p>

      {!pkg.has_deployment ? (
        <div className="flex items-center justify-between flex-wrap gap-4 p-4 rounded-lg bg-black border border-divider">
          <p className="text-sm text-light-gray">{pkg.message}</p>
          <button onClick={handleDeploy} disabled={busy} data-testid="pkg-deploy-btn"
            className="px-5 py-2.5 rounded-lg bg-lime text-black font-bold text-sm hover:bg-lime/90 disabled:opacity-50">
            {busy ? 'Starting…' : 'Deploy & Start Tracking'}
          </button>
        </div>
      ) : (
        <>
          {pkg.base_url_help && (
            <p className="mb-4 p-3 rounded-lg bg-orange/10 border border-orange/40 text-orange text-sm" data-testid="pkg-baseurl-help">
              {pkg.base_url_help}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
            {pkg.fields.map((f) => <CopyField key={f.key} field={f} />)}
            <div className="p-3.5 rounded-lg bg-black border border-divider" data-testid="pkg-field-promo">
              <p className="text-xs text-muted uppercase tracking-wide font-semibold mb-1">Promo Code (optional)</p>
              <div className="flex gap-2">
                <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="e.g. NOCOOL50"
                  data-testid="pkg-promo-input"
                  className="flex-1 bg-card-bg border border-divider rounded px-2.5 py-1.5 text-sm text-white focus:border-lime focus:outline-none" />
                <button onClick={savePromo} data-testid="pkg-promo-save" className="text-xs text-lime font-bold hover:underline">Save</button>
              </div>
              <p className="text-[13px] text-gray mt-1.5">A promo code customers mention is another way to know this campaign brought them in.</p>
            </div>
            <div className="p-3.5 rounded-lg bg-black border border-divider" data-testid="pkg-field-tracking-phone">
              <p className="text-xs text-muted uppercase tracking-wide font-semibold mb-1">Tracking Phone Number</p>
              {pkg.tracking_phone ? (
                <code className="text-sm text-lime">{pkg.tracking_phone}</code>
              ) : (
                <p className="text-xs text-gray">Add a tracking phone in <Link to="/my-brand" className="text-orange underline">My Brand</Link> — a separate number used only in ads so calls can be tracked.</p>
              )}
              <p className="text-[13px] text-gray mt-1.5">Use this number in ads instead of your main line so you know which calls came from marketing.</p>
            </div>
          </div>

          {/* Launch mode selector */}
          <div className="pt-4 border-t border-divider">
            <p className="text-white font-bold mb-3">How do you want to launch?</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              {MODES.map((m) => (
                <button key={m.id} onClick={() => handleMode(m.id)} disabled={m.soon}
                  data-testid={`launch-mode-${m.id}`}
                  className={`text-left p-4 rounded-lg border-2 transition-colors ${
                    m.soon ? 'border-divider bg-black opacity-50 cursor-not-allowed'
                      : pkg.launch_mode === m.id ? 'border-lime bg-lime/5' : 'border-divider bg-black hover:border-lime/50'
                  }`}>
                  <p className="text-white font-bold text-sm flex items-center gap-2 mb-1">
                    <m.icon size={15} className="text-orange" /> {m.label}
                    {m.soon && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400">COMING SOON</span>}
                  </p>
                  <p className="text-xs text-gray">{m.desc}</p>
                </button>
              ))}
            </div>

            {pkg.launch_mode === 'manual' && (
              <div className="p-4 rounded-lg bg-black border border-divider" data-testid="manual-launch-panel">
                <p className="text-sm text-light-gray leading-relaxed">
                  <span className="text-white font-bold">Manual launch:</span> copy your personalized assets from below, paste them into your ad platforms and emails,
                  use the campaign names and links from the package above, and paste the booking note into your CRM on every booked job.
                  Then enter your ad spend and results on the Campaign Performance card — GritClicks does the math.
                </p>
              </div>
            )}

            {pkg.launch_mode === 'guided' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="guided-launch-panel">
                {[['meta', 'Launch on Meta (Facebook/Instagram)'], ['google', 'Launch on Google Ads']].map(([key, title]) => (
                  <div key={key} className="p-4 rounded-lg bg-black border border-divider">
                    <p className="text-white font-bold mb-3">{title}</p>
                    <ol className="space-y-2">
                      {pkg.guided_steps[key].map((step, i) => (
                        <li key={i} className="text-sm text-light-gray flex gap-2.5">
                          <span className="text-lime font-black shrink-0">{i + 1}.</span> {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {msg && <p className="text-sm text-lime mt-4" data-testid="pkg-message">{msg}</p>}
    </div>
  );
};
